// src/oura/components/docs/write/WriteSheet.tsx
//
// A write view of the doc drawer (Docs phase 2, lane 3), the way Pick a code
// is: the sheet's own header (the doc's lead and status, the view's title),
// then the view's body and foot. The board's shell draws "‹ {doc title}" in
// Row A, so Escape and Back return to the doc. A doc the write switch or its
// file type rules out shows no write view, only the way back.

import type { ReactNode } from "react";
import type { BusinessDocuments, DocumentDetail, StaffDocument } from "@service/aeo/clientDocs";
import { DrawerBlock, DrawerFoot, DrawerHead, PillButton, type DrawerStatus } from "@oura/ui";
import { ClientEmailView } from "./ClientEmailView";
import { DocMessageView } from "./DocMessageView";
import { NameLinesView } from "./NameLinesView";
import { QuestionsView } from "./QuestionsView";
import { ShareView } from "./ShareView";
import { WRITE_VIEW_TITLE, canShare, canWrite, isThreadView, type DocWriteView } from "./model";
import { ThreadSheet } from "../threads/ThreadSheet";
import { threadIdOf } from "../threads/model";

export function WriteSheet({
    view,
    doc,
    detail,
    loading,
    businessId,
    businessName,
    now,
    plan = null,
    lead,
    status,
    onBusy,
    onView,
}: {
    view: DocWriteView;
    doc: StaffDocument;
    detail: DocumentDetail | null;
    /** The doc's threads are still on their way. */
    loading: boolean;
    businessId: string;
    businessName: string;
    now: number;
    /** The board's signed plan, for a monthly doc's "Month N" (ourteam-backend#6868). */
    plan?: BusinessDocuments["plan"];
    /** The doc's IconSquare, as the doc's own header has it. */
    lead: ReactNode;
    status: DrawerStatus;
    onBusy: (busy: boolean) => void;
    /** Another write view, or "doc" to go back. */
    onView: (view: DocWriteView | "doc") => void;
}) {
    // A thread view draws its own header: the thread's title and state, not the doc's (lane 4).
    if (isThreadView(view) && canWrite(doc)) {
        // Keyed per thread, so a half-typed reply never carries to the next thread.
        // KEYED BY THE DOCUMENT, not only the view (Jack, #3964). The drawer
        // can change documents without unmounting this sheet, and React then
        // keeps the child's state: one document's edited email could be sent
        // for another, a previous client's typed address could be preselected
        // when sharing, and a destructive confirm could survive onto the wrong
        // doc.
        return <ThreadSheet key={`${doc.id}:${view}`} threadId={threadIdOf(view) ?? ""} doc={doc} detail={detail} loading={loading} onBusy={onBusy} onView={onView} />;
    }

    const head = <DrawerHead lead={lead} leadKind="square" title={isThreadView(view) ? "Comment" : WRITE_VIEW_TITLE[view]} status={status} />;

    // A PDF opens the Share view only: it has no comments for Oura to write (ShareDonePanel).
    if ((!canWrite(doc) && !(view === "share" && canShare(doc))) || isThreadView(view)) {
        return (
            <>
                {head}
                <DrawerBlock>
                    <p className="m-0 text-gray-700">Oura can&rsquo;t write to this file.</p>
                </DrawerBlock>
                <DrawerFoot second={<PillButton onClick={() => onView("doc")}>Back to the doc</PillButton>} />
            </>
        );
    }

    let body: ReactNode;
    switch (view) {
        case "questions":
            body = <QuestionsView key={`${doc.id}:questions`} doc={doc} detail={detail} loading={loading} onBusy={onBusy} onView={onView} />;
            break;
        case "lines":
            body = <NameLinesView doc={doc} detail={detail} businessName={businessName} now={now} plan={plan} onBusy={onBusy} onView={onView} />;
            break;
        case "share":
            body = <ShareView key={`${doc.id}:share`} doc={doc} businessId={businessId} onBusy={onBusy} onView={onView} />;
            break;
        case "review":
        case "chaser":
            // Keyed by kind, so its draft and its edits never carry from one kind to the other.
            body = <DocMessageView key={`${doc.id}:${view}`} view={view} doc={doc} businessId={businessId} now={now} onBusy={onBusy} onView={onView} />;
            break;
        case "client-email":
            // Keyed by the doc: its draft, its edits and a pending send never carry to another doc.
            body = <ClientEmailView key={`${doc.id}:client-email`} doc={doc} onBusy={onBusy} onView={onView} />;
            break;
    }

    return (
        <>
            {head}
            {body}
        </>
    );
}
